/**
 * Shopify's own shipping quote, requested only by a customer click.
 * This module never adds, changes or clears cart items.
 */
export function cartFingerprint(cart) {
  return JSON.stringify([
    cart.token,
    cart.currency,
    cart.total_price,
    cart.total_discount,
    cart.items.map((item) => [item.key, item.quantity, item.final_line_price]),
  ]);
}

/** Decimal rate prices are major currency units, not cents. */
export function normalizeRate(rate, cartCurrency, allowFallback = false) {
  const currency = rate.currency || (allowFallback ? cartCurrency : null);
  if (!currency || !/^[A-Z]{3}$/.test(currency)) throw new Error('currency');
  if (!/^\d+(?:\.\d+)?$/.test(String(rate.price))) throw new Error('invalid');
  const price = Number(rate.price);
  if (!Number.isFinite(price) || price < 0) throw new Error('invalid');
  return { name: String(rate.presentment_name || rate.name || ''), currency, price };
}

/** Wait without leaving pending timers after cancellation. */
function wait(milliseconds, signal) {
  return new Promise((resolve, reject) => {
    const abort = () => {
      clearTimeout(timer);
      signal.removeEventListener('abort', abort);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', abort);
      resolve();
    }, milliseconds);
    signal.addEventListener('abort', abort, { once: true });
    if (signal.aborted) abort();
  });
}

/**
 * @param {{root: URL, address: URLSearchParams, signal: AbortSignal,
 * fetcher?: typeof fetch, pause?: typeof wait}} options
 */
export async function requestShippingQuote({ root, address, signal, fetcher = fetch, pause = wait }) {
  const read = async (path, method = 'GET', query) => {
    const url = new URL(path, root);
    if (query) url.search = query.toString();
    const response = await fetcher(url, {
      method,
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      signal,
    });
    if (!response.ok) throw new Error(response.status === 422 ? 'address' : 'network');
    return response.json();
  };
  const before = await read('cart.js');
  if (!Array.isArray(before.items) || !before.items.length) throw new Error('empty');
  if (!before.items.some((item) => item.requires_shipping)) throw new Error('digital');
  const fingerprint = cartFingerprint(before);
  await read('cart/prepare_shipping_rates.json', 'POST', address);
  for (let attempt = 0; attempt < 8; attempt += 1) {
    await pause(Math.min(400 + attempt * 400, 2400), signal);
    const result = await read('cart/async_shipping_rates.json', 'GET', address);
    if (result === null) continue;
    if (!Array.isArray(result.shipping_rates)) throw new Error('invalid');
    const after = await read('cart.js');
    if (cartFingerprint(after) !== fingerprint) throw new Error('changed');
    return { rates: result.shipping_rates, currency: before.currency, fingerprint };
  }
  throw new Error('timeout');
}

const text = {
  de: {
    loading: 'Versandarten werden berechnet …',
    ready: 'Versandarten für diesen Warenkorb und das eingegebene Lieferziel:',
    none: 'Für dieses Lieferziel wurde keine Versandart zurückgegeben. Bitte prüfe Land, Region und Postleitzahl oder kontaktiere support@nized.de.',
    empty: 'Lege zuerst die gewünschten Artikel in deinen Warenkorb.',
    digital: 'Für diesen Warenkorb ist kein Versand erforderlich.',
    address: 'Bitte prüfe Land, Region und Postleitzahl.',
    changed: 'Der Warenkorb oder das Lieferziel wurde geändert. Bitte berechne die Versandkosten erneut.',
    currency: 'Die Währung der Versandberechnung konnte nicht bestätigt werden. Bitte prüfe die Versandkosten im Checkout oder frage support@nized.de.',
    timeout: 'Die Berechnung dauert zu lange. Bitte versuche es erneut.',
    network: 'Die Versandkosten konnten gerade nicht geladen werden. Bitte versuche es erneut.',
    invalid: 'Die Versandberechnung konnte nicht eindeutig gelesen werden. Bitte versuche es erneut.',
    province: 'Bitte wählen',
    rate: 'Versand',
  },
  en: {
    loading: 'Calculating shipping options …',
    ready: 'Shipping options for this cart and the destination you entered:',
    none: 'No shipping option was returned for this destination. Check the country, region and postal code, or contact support@nized.de.',
    empty: 'Add the items you want to your cart first.',
    digital: 'This cart does not require shipping.',
    address: 'Please check the country, region and postal code.',
    changed: 'The cart or destination changed. Please calculate shipping again.',
    currency: 'The currency of the shipping quote could not be confirmed. Please check shipping at checkout or contact support@nized.de.',
    timeout: 'The calculation is taking too long. Please try again.',
    network: 'Shipping costs could not be loaded. Please try again.',
    invalid: 'The shipping response could not be read reliably. Please try again.',
    province: 'Please select',
    rate: 'Shipping',
  },
};

const BaseElement = globalThis.HTMLElement || class {};
class NizedShippingCalculator extends BaseElement {
  static active = null;
  #controller;
  #events;
  #expiry;
  #fingerprint;
  #status;
  #rates;
  #country;
  #province;
  #zip;
  #messages;
  #invalidate = () => this.#clear(true);
  #cartRendered = (event) => {
    const cart = event.detail?.cart;
    if (!cart || !this.#fingerprint || cartFingerprint(cart) !== this.#fingerprint) this.#clear(true);
  };

  connectedCallback() {
    this.#events?.abort();
    this.#events = new AbortController();
    this.#status = this.querySelector('[data-status]');
    this.#rates = this.querySelector('[data-rates]');
    this.#country = this.querySelector('[data-country]');
    this.#province = this.querySelector('[data-province]');
    this.#zip = this.querySelector('[data-zip]');
    this.#messages = text[this.dataset.language] || text.de;
    const options = { signal: this.#events.signal };
    this.querySelector('[data-calculate]').addEventListener('click', () => this.#calculate(), options);
    this.#country.addEventListener('change', () => { this.#provinces(); this.#clear(true); }, options);
    this.#province.addEventListener('change', this.#invalidate, options);
    this.#zip.addEventListener('input', this.#invalidate, options);
    for (const event of ['cart:update', 'discount:update', 'quantity-selector:update']) {
      document.addEventListener(event, this.#invalidate, options);
    }
    document.addEventListener('nized:cart-rendered', this.#cartRendered, options);
    window.addEventListener('pageshow', this.#invalidate, options);
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) this.#clear(true);
    }, options);
    this.#provinces();
  }

  disconnectedCallback() {
    this.#events?.abort();
    this.#controller?.abort();
    clearTimeout(this.#expiry);
    if (NizedShippingCalculator.active === this) NizedShippingCalculator.active = null;
  }

  #clear(notify) {
    const hadResult = this.#fingerprint || this.#controller;
    this.#controller?.abort();
    this.#controller = null;
    this.#fingerprint = null;
    clearTimeout(this.#expiry);
    this.#rates?.replaceChildren();
    if (this.#status) this.#status.textContent = notify && hadResult ? this.#messages.changed : '';
  }

  #provinces() {
    let provinces = [];
    try { provinces = JSON.parse(this.#country.selectedOptions[0]?.dataset.provinces || '[]'); } catch { /* Ask Shopify to validate. */ }
    this.#province.replaceChildren(new Option(this.#messages.province, ''));
    for (const [value, label] of provinces) this.#province.add(new Option(label, value));
    const hasProvinces = provinces.length > 0;
    this.querySelector('[data-province-wrapper]').hidden = !hasProvinces;
    this.#province.disabled = !hasProvinces;
  }

  async #calculate() {
    if (NizedShippingCalculator.active && NizedShippingCalculator.active !== this) {
      NizedShippingCalculator.active.#clear(true);
    }
    NizedShippingCalculator.active = this;
    this.#clear(false);
    if (!this.#country.value || this.#country.value === '---') {
      this.#status.textContent = this.#messages.address;
      this.#country.focus();
      return;
    }
    const root = new URL(this.dataset.root || '/', window.location.origin);
    if (root.origin !== window.location.origin) return;
    if (!root.pathname.endsWith('/')) root.pathname += '/';
    const address = new URLSearchParams({
      'shipping_address[country]': this.#country.value,
      'shipping_address[province]': this.#province.disabled ? '' : this.#province.value,
      'shipping_address[zip]': this.#zip.value.trim(),
    });
    const controller = new AbortController();
    this.#controller = controller;
    const timeout = setTimeout(() => controller.abort(new Error('timeout')), 25000);
    const button = this.querySelector('[data-calculate]');
    button.disabled = true;
    this.setAttribute('aria-busy', 'true');
    this.#status.textContent = this.#messages.loading;
    try {
      const quote = await requestShippingQuote({ root, address, signal: controller.signal });
      if (this.#controller !== controller) return;
      const rates = quote.rates.map((rate) => normalizeRate(
        rate, quote.currency, this.dataset.allowCartCurrencyFallback === 'true',
      ));
      const fragment = document.createDocumentFragment();
      for (const rate of rates) {
        const row = document.createElement('li');
        const money = new Intl.NumberFormat(this.dataset.language || 'de', {
          style: 'currency', currency: rate.currency, currencyDisplay: 'code',
        }).format(rate.price);
        row.textContent = `${rate.name || this.#messages.rate}: ${money}`;
        fragment.append(row);
      }
      this.#rates.replaceChildren(fragment);
      this.#fingerprint = quote.fingerprint;
      this.#status.textContent = rates.length ? this.#messages.ready : this.#messages.none;
      this.#expiry = setTimeout(() => this.#clear(true), 5 * 60 * 1000);
    } catch (error) {
      if (this.#controller !== controller) return;
      const code = controller.signal.reason?.message === 'timeout' ? 'timeout' : error.message;
      this.#status.textContent = this.#messages[code] || this.#messages.network;
    } finally {
      clearTimeout(timeout);
      if (this.#controller === controller) this.#controller = null;
      button.disabled = false;
      this.removeAttribute('aria-busy');
    }
  }
}

if (globalThis.customElements && !customElements.get('nized-shipping-calculator')) {
  customElements.define('nized-shipping-calculator', NizedShippingCalculator);
}
